/**
 * LiveFlight Search Module
 * Handles search parameters: airports, dates, travelers, cabin preferences
 */

import {
  create_location,
  sanitize_origin_destination,
  create_origin_destination,
  TRIP_TYPES,
  reset_origin_destinations_for_trip_type,
} from "../utilities/origin-destination";

// Re-export for backward compatibility
export { create_location, create_origin_destination };

// Default travelers initialization
let default_adults = 2,
  default_travelers = [];

for (let index = 1; index <= default_adults; index++) {
  default_travelers.push({
    id: index,
    travelerType: "ADULT",
    fareOptions: ["STANDARD"],
  });
}

/**
 * Search state properties
 */
export const state = {
  // Travelers
  adults: default_adults,
  children: [],
  travelers: default_travelers,

  // Origin-Destinations: list of { departure: {iata_code, label}, arrival: {iata_code, label}, date }
  // For round-trip: [0] = outbound (A→B), [1] = return (B→A)
  // For one-way: [0] = single leg
  // For multi-itinerary: [0], [1], [2], ... = each leg
  origin_destinations: [
    create_origin_destination(),
    create_origin_destination(),
  ],
  return_flight: true,

  // Search options
  max_number_of_connections: 2,
  included_checked_bags_only: 0,
  cabin: "",
  trip_type: "round_trip",
};

/**
 * Custom mutations for search state
 * Note: Basic mutations (setter for each property) are auto-generated by vuex_store_config
 */
export const mutations = {
  // Atomic mutation: updates trip_type, return_flight, and origin_destinations in a single commit.
  // Prevents the triple re-render cascade that occurs when these are committed separately.
  set_trip_type(state, trip_type) {
    state.trip_type = trip_type;
    state.return_flight = trip_type === TRIP_TYPES.ROUND_TRIP;
    state.origin_destinations = reset_origin_destinations_for_trip_type(
      state.origin_destinations,
      trip_type,
    );
  },

  // Sanitizing mutation for origin_destinations — use for external/untrusted data
  // (localStorage load, computed setters that accept arbitrary input)
  origin_destinations(state, data) {
    if (!data || !Array.isArray(data)) return;

    const adjusted = data.map(sanitize_origin_destination);

    // Ensure each subsequent leg's date is not before the previous one
    // YYYY-MM-DD strings are lexicographically sortable, no moment needed
    for (let i = 1; i < adjusted.length; i++) {
      const { date: current_date } = adjusted[i];
      const { date: previous_date } = adjusted[i - 1];
      if (current_date && previous_date && current_date < previous_date) {
        adjusted[i] = { ...adjusted[i], date: previous_date };
      }
    }

    state.origin_destinations = adjusted;
  },
};

/**
 * Keys to persist in local storage
 */
export const localStorageKeys = [
  "adults",
  "children",
  "travelers",
  "origin_destinations",
  "return_flight",
  "cabin",
  "trip_type",
];

/**
 * Sanitize the search portion of the loaded state.
 * Returns a new object with the sanitized search keys - does NOT mutate the input.
 *
 * Handles two scenarios:
 * 1. Legacy migration: old flat localStorage keys (departure_airport, etc.)
 *    are converted into the new origin_destinations structure.
 * 2. Data sanitization: corrupted origin_destinations entries (Arrays, strings)
 *    are normalized to proper {iata_code, label} objects.
 *
 * @param {Object} loaded - The loaded state object (not mutated)
 * @returns {Object} Sanitized search keys to spread onto the store state
 */
export function sanitize_search_store(loaded) {
  const result = {};

  // --- Legacy migration ---
  // Users upgrading from the old state format will have orphan keys
  // (departure_airport, arrival_airport, etc.) on the loaded state.
  // Construct origin_destinations from those keys.
  if (
    loaded.departure_airport !== undefined ||
    loaded.arrival_airport !== undefined
  ) {
    const departure = create_location({
      iata_code: loaded.departure_airport || "",
      label: loaded.departure_airport_label || "",
    });
    const arrival = create_location({
      iata_code: loaded.arrival_airport || "",
      label: loaded.arrival_airport_label || "",
    });

    const departure_date = loaded.departure_date || "";
    const return_date = loaded.return_date || "";

    const outbound = create_origin_destination({
      departure,
      arrival,
      date: departure_date,
    });

    if (return_date) {
      const return_leg = create_origin_destination({
        departure: arrival,
        arrival: departure,
        date: return_date,
      });
      result.origin_destinations = [outbound, return_leg];
      result.return_flight = true;
      result.trip_type = TRIP_TYPES.ROUND_TRIP;
    } else {
      result.origin_destinations = [outbound];
      result.return_flight = false;
      result.trip_type = TRIP_TYPES.ONE_WAY;
    }
  }

  // --- Data sanitization ---
  // Fix corrupted origin_destinations where departure/arrival may be
  // Arrays, strings, or other unexpected types instead of {iata_code, label}.
  const ods = result.origin_destinations || loaded.origin_destinations;
  if (ods && Array.isArray(ods) && ods.length > 0) {
    result.origin_destinations = ods.map(sanitize_origin_destination);

    // --- Trip type alignment ---
    // Ensure origin_destinations length matches the persisted trip_type
    result.origin_destinations = reset_origin_destinations_for_trip_type(
      result.origin_destinations,
      result.trip_type || loaded.trip_type,
    );
  }

  return result;
}
